import { Suspense, useId, useMemo } from 'react'
import { ErrorLevel, PlatformErrorBoundary } from '../../errors'
import { ComponentPageStateProvider } from '../context'
import { useApiClient } from '../hooks'
import { componentLoader, ComponentLoadingState } from '../registry'

export interface PlatformComponentProps {
  packageName: string
  name: string
  /**
   * Optional disambiguator for pages mounting more than one instance of
   * the same registered component. Folded into the auto-wrapped
   * `ComponentPageStateProvider`'s `initialMetadata.role` so consumers
   * can resolve a specific slot via `useComponentByRole(name, role)`.
   */
  componentRole?: string
}

export function PlatformComponent<ComponentProps>({
  packageName,
  name,
  componentRole,
  ...componentProps
}: PlatformComponentProps & ComponentProps) {
  return (
    <PlatformErrorBoundary level={ErrorLevel.COMPONENT}>
      <Suspense fallback={<></>}>
        <PlatformComponentLoader
          packageName={packageName}
          name={name}
          componentRole={componentRole}
          {...componentProps}
        />
      </Suspense>
    </PlatformErrorBoundary>
  )
}

// `PlatformComponentLoader` guards on `useApiClient()` returning null
// (early-init) and delegates the real loading work to
// `PlatformComponentInner` below. Splitting the apiClient null-check
// off this way lets the inner component own all subsequent hooks
// unconditionally — `useId`, `useMemo` for `initialMetadata`, etc. —
// without tripping `react-hooks/rules-of-hooks`.
export function PlatformComponentLoader<ComponentProps>({
  packageName,
  name,
  componentRole,
  ...componentProps
}: PlatformComponentProps & ComponentProps) {
  const apiClient = useApiClient()
  if (!apiClient) return null
  return (
    <PlatformComponentInner
      apiClient={apiClient}
      packageName={packageName}
      name={name}
      componentRole={componentRole}
      {...(componentProps as ComponentProps)}
    />
  )
}

function PlatformComponentInner<ComponentProps>({
  apiClient,
  packageName,
  name,
  componentRole,
  ...componentProps
}: {
  apiClient: NonNullable<ReturnType<typeof useApiClient>>
} & PlatformComponentProps &
  ComponentProps) {
  const baseId = useId()
  const componentId = `${packageName}:${name}:${baseId}`

  const loadingState = componentLoader.loadComponent(
    apiClient,
    packageName,
    name,
  )

  const { Component } = getValueSuspense(loadingState)
  const metadata = getMetadataSuspense(loadingState)

  // Infer componentType from metadata or packageName/name pattern
  const componentType = name

  // `initialMetadata` flows into `ComponentPageStateProvider`'s
  // `useLayoutEffect` dep array (register on mount, unregister on
  // cleanup). Without memoisation, a fresh object on every render
  // re-fires that effect → unregister/register cycle → setComponents
  // → re-render → React #185 (max update depth) lands in any consumer
  // subscribing to PageStateContext (esp. via the PLA-395
  // `useComponentByRole` selector hook). Memoise on the primitive
  // inputs so the reference stays stable unless metadata or the role
  // actually changed.
  const initialMetadata = useMemo(() => {
    const hasRole =
      typeof componentRole === 'string' && componentRole.length > 0
    if (!metadata && !hasRole) return undefined
    return {
      ...metadata,
      description: metadata?.description || metadata?.name,
      ...(hasRole ? { role: componentRole } : {}),
    }
  }, [metadata, componentRole])

  return (
    <ComponentPageStateProvider
      componentId={componentId}
      componentType={componentType}
      initialMetadata={initialMetadata}
    >
      <Component {...componentProps} />
    </ComponentPageStateProvider>
  )
}

const getValueSuspense = (loadingState: ComponentLoadingState) => {
  if (loadingState.state === 'loading') {
    throw loadingState.promise
  } else if (loadingState.state === 'error') {
    throw loadingState.error
  }
  return loadingState.value
}

const getMetadataSuspense = (loadingState: ComponentLoadingState) => {
  if (loadingState.state === 'loading') {
    throw loadingState.promise
  } else if (loadingState.state === 'error') {
    throw loadingState.error
  }
  return loadingState.metadata
}
